import { motion } from "framer-motion";
import { useEffect } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { aiServices, type AiService } from "@/lib/aiServices";

function setMeta(name: string, content: string) {
  let tag = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.name = name;
    document.head.appendChild(tag);
  }
  tag.content = content;
}

function AiServicePage({ service }: { service: AiService }) {
  const related = aiServices.filter((item) => item.href !== service.href).slice(0, 3);

  useEffect(() => {
    document.title = `${service.title} | Variable Soft`;
    setMeta(
      "description",
      `${service.title} by Variable Soft. ${service.tagline} Build practical AI automation, integrations and business workflows for growing companies.`,
    );
  }, [service]);

  return (
    <div className="min-h-screen bg-white text-slate-950">
      <Navigation />

      <section className="relative overflow-hidden bg-slate-950 pt-32 pb-20 text-white">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_15%_15%,rgba(34,211,238,0.18),transparent_55%),radial-gradient(760px_circle_at_85%_70%,rgba(59,130,246,0.18),transparent_55%)]" />
          <div className="absolute inset-0 opacity-[0.12] [background-image:linear-gradient(rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:52px_52px]" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65 }}
              className="lg:col-span-7"
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-black uppercase tracking-widest text-cyan-200">
                <i className={`${service.icon} text-sm`} />
                AI Services
              </span>

              <h1 className="mt-7 text-4xl font-black leading-tight sm:text-5xl lg:text-6xl">
                {service.title}
              </h1>

              <p className="mt-5 max-w-3xl text-lg leading-8 text-white/72">
                {service.description}
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href="/getintouch"
                  className="inline-flex items-center justify-center rounded-2xl bg-cyan-400 px-6 py-3 font-bold text-slate-950 shadow-lg transition hover:bg-cyan-300"
                >
                  Discuss AI Project
                </a>
                <a
                  href="/services/software-development"
                  className="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/5 px-6 py-3 font-semibold text-white transition hover:bg-white/10"
                >
                  Software Services
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.1 }}
              className="lg:col-span-5"
            >
              <div className="rounded-[30px] border border-white/10 bg-white/[0.06] p-6 shadow-2xl backdrop-blur-xl">
                <div className="grid h-16 w-16 place-items-center rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-600 text-2xl text-white">
                  <i className={service.icon} />
                </div>
                <h2 className="mt-5 text-2xl font-extrabold">{service.tagline}</h2>
                <div className="mt-5 grid gap-3">
                  {service.heroPoints.map((point) => (
                    <div key={point} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.05] p-4">
                      <span className="mt-1 h-2.5 w-2.5 rounded-full bg-cyan-300" />
                      <span className="text-sm font-semibold text-white/78">{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-3">
            {[
              { title: "Useful Use Cases", items: service.useCases, icon: "fas fa-bullseye" },
              { title: "What We Deliver", items: service.deliverables, icon: "fas fa-list-check" },
              { title: "Business Outcomes", items: service.outcomes, icon: "fas fa-chart-simple" },
            ].map((block, index) => (
              <motion.div
                key={block.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.55, delay: index * 0.08 }}
                className="rounded-[28px] border border-slate-200 bg-slate-50 p-6"
              >
                <div className="grid h-12 w-12 place-items-center rounded-2xl bg-slate-950 text-white">
                  <i className={block.icon} />
                </div>
                <h2 className="mt-5 text-2xl font-extrabold text-slate-950">{block.title}</h2>
                <div className="mt-5 space-y-3">
                  {block.items.map((item) => (
                    <div key={item} className="flex gap-3 rounded-2xl bg-white p-4 shadow-sm">
                      <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-primary" />
                      <span className="text-sm font-semibold leading-relaxed text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-slate-50 py-20">
        <div className="absolute inset-0 opacity-[0.22] [background-image:linear-gradient(rgba(15,23,42,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.06)_1px,transparent_1px)] [background-size:48px_48px]" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-start gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <span className="inline-flex rounded-full border border-blue-100 bg-white px-4 py-2 text-xs font-black uppercase tracking-widest text-primary">
                Implementation Stack
              </span>
              <h2 className="mt-6 text-3xl font-extrabold leading-tight text-slate-950 sm:text-4xl">
                Practical AI connected to your current systems.
              </h2>
              <p className="mt-4 text-lg leading-8 text-slate-600">
                We focus on useful AI that can be maintained, monitored and improved. The goal is not a demo - it is an AI workflow that supports your real operations.
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="grid gap-3 sm:grid-cols-2">
                {service.stack.map((item) => (
                  <div key={item} className="rounded-2xl border border-slate-200 bg-white p-4 font-bold text-slate-800 shadow-sm">
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-14 rounded-[30px] border border-slate-200 bg-white p-6 shadow-[0_30px_90px_-60px_rgba(15,23,42,0.45)]">
            <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-center">
              <div>
                <h2 className="text-2xl font-extrabold text-slate-950">Need a custom AI roadmap?</h2>
                <p className="mt-2 text-slate-600">
                  Share your workflow, CRM, data sources or support process. We will suggest where AI can create value without overcomplicating your operations.
                </p>
              </div>
              <a
                href="/getintouch"
                className="inline-flex items-center justify-center rounded-2xl bg-slate-950 px-6 py-3 font-bold text-white transition hover:bg-slate-800"
              >
                Start AI Consultation
              </a>
            </div>
          </div>
        </div>
      </section>

      {service.href === "/services/ai-chatbot-development" && (
        <section className="bg-white py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-[30px] border border-slate-200 bg-gradient-to-br from-blue-50 via-white to-cyan-50 p-6 shadow-[0_24px_80px_-55px_rgba(15,23,42,0.35)]">
              <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-center">
                <div>
                  <span className="text-xs font-black uppercase tracking-widest text-primary">VSCRM.ai Reference</span>
                  <h2 className="mt-3 text-2xl font-extrabold text-slate-950">
                    AI chatbot capability connected with our VSCRM.ai ecosystem.
                  </h2>
                  <p className="mt-3 max-w-3xl text-slate-600">
                    For CRM-focused chatbot and AI response workflows, we can reference VSCRM.ai-style capabilities such as lead capture, conversation intelligence, CRM sync, follow-up automation and business-specific knowledge responses.
                  </p>
                </div>
                <a
                  href="https://vscrm.ai"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-2xl bg-slate-950 px-6 py-3 font-bold text-white transition hover:bg-slate-800"
                >
                  Visit VSCRM.ai
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <span className="text-xs font-black uppercase tracking-widest text-primary">More AI Services</span>
              <h2 className="mt-2 text-3xl font-extrabold text-slate-950">Explore related AI capabilities</h2>
            </div>
            <a href="/getintouch" className="text-sm font-bold text-primary hover:underline">
              Talk to an AI consultant
            </a>
          </div>

          <div className="grid gap-5 md:grid-cols-3">
            {related.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="group rounded-[26px] border border-slate-200 bg-slate-50 p-6 transition hover:-translate-y-1 hover:border-blue-200 hover:bg-blue-50/50"
              >
                <div className="grid h-12 w-12 place-items-center rounded-2xl bg-white text-primary shadow-sm">
                  <i className={item.icon} />
                </div>
                <h3 className="mt-5 text-xl font-extrabold text-slate-950 group-hover:text-primary">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.tagline}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export function AIChatbotDevelopmentPage() {
  return <AiServicePage service={aiServices[0]} />;
}

export function WhatsappAIResponsePage() {
  return <AiServicePage service={aiServices[1]} />;
}

export function AIAgentAutomationPage() {
  return <AiServicePage service={aiServices[2]} />;
}

export function GenerativeAIIntegrationPage() {
  return <AiServicePage service={aiServices[3]} />;
}

export function AICrmLeadScoringPage() {
  return <AiServicePage service={aiServices[4]} />;
}

export function AIDocumentProcessingPage() {
  return <AiServicePage service={aiServices[5]} />;
}

export function AIDataAnalyticsPage() {
  return <AiServicePage service={aiServices[6]} />;
}
